/**
 * Admin Payment Service
 * Handles all payment method-related admin API calls
 */

import { getApi, postApi, updateApi, deleteApi, patchApi } from '../adminApi';

/**
 * Get all payment methods
 * @returns {Promise}
 */
export const getPaymentMethods = async () => {
  return getApi('payment-methods');
};

/**
 * Get a single payment method by ID
 * @param {string} paymentMethodId - Payment method ID
 * @returns {Promise}
 */
export const getPaymentMethod = async (paymentMethodId) => {
  return getApi(`payment-methods/${paymentMethodId}`);
};

/**
 * Update a payment method
 * @param {string} paymentMethodId - Payment method ID
 * @param {Object|FormData} data - Update data
 * @returns {Promise}
 */
export const updatePaymentMethod = async (paymentMethodId, data) => {
  return updateApi(`payment-methods/${paymentMethodId}`, data);
};

/**
 * Create a new payment method
 * @param {Object} data - Payment method data
 * @returns {Promise}
 */
export const createPaymentMethod = async (data) => {
  return postApi('payment-methods', data);
};

/**
 * Toggle payment method status
 * @param {string} paymentMethodId - Payment method ID
 * @returns {Promise}
 */
export const togglePaymentMethodStatus = async (paymentMethodId) => {
  return patchApi(`payment-methods/${paymentMethodId}/toggle-status`);
};

/**
 * Delete a payment method
 * @param {string} paymentMethodId - Payment method ID
 * @returns {Promise}
 */
export const deletePaymentMethod = async (paymentMethodId) => {
  return deleteApi(`payment-methods/${paymentMethodId}`);
};

/**
 * Get payment method mappings
 * @param {Object} params - Query parameters
 * @returns {Promise}
 */
export const getPaymentMethodMappings = async (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return getApi(`payment-methods/mapping?${queryParams}`);
};

/**
 * Create payment method mapping
 * @param {Object} mappingForm - Mapping data
 * @returns {Promise}
 */
export const createPaymentMethodMapping = async (mappingForm) => {
  return postApi('payment-methods/mapping', mappingForm);
};

/**
 * Create bulk payment method mappings
 * @param {Array} mappings - Array of mapping objects
 * @returns {Promise}
 */
export const createBulkPaymentMethodMappings = async (mappings) => {
  return postApi('payment-methods/mapping/bulk', { mappings });
};

/**
 * Update payment method mapping status
 * @param {string} mappingId - Mapping ID
 * @param {string} status - New status
 * @returns {Promise}
 */
export const updatePaymentMethodMappingStatus = async (mappingId, status) => {
  return updateApi(`payment-methods/mapping/${mappingId}`, { status });
};

/**
 * Delete payment method mapping
 * @param {string} mappingId - Mapping ID
 * @returns {Promise}
 */
export const deletePaymentMethodMapping = async (mappingId) => {
  return deleteApi(`payment-methods/mapping/${mappingId}`);
};

